import React, { useEffect, useState, useRef } from "react";
import { Box, CircularProgress, Typography, Paper, Pagination } from "@mui/material";
import { useSelector } from "react-redux";
import moment from "moment";
import * as mainService from "../../../../services/mainService";

const Timeline = ({ appointmentId, patientId }) => {
  const [prescriptions, setPrescriptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 5;
  const { userData } = useSelector((state) => ({ userData: state.auth.userData }));
  const hasFetched = useRef(false);

  useEffect(() => {
    const fetchPrescriptions = async () => {
      if ((!appointmentId && !patientId) || hasFetched.current) return;
      hasFetched.current = true;
      setLoading(true);
      try {
        const value = appointmentId 
          ? { token: userData.token, appointmentId, date: moment().format("YYYY-MM-DD") }
          : { token: userData.token, patientId, date: moment().format("YYYY-MM-DD") };
        const res = await mainService.getPatientPrescriptionApi(value);
        if (res.response_type === "success") {
          const responseData = res.response?.data;
          // Check if backend returned paginated data
          if (responseData?.data && Array.isArray(responseData.data)) {
            setPrescriptions(responseData.data || []);
            setTotalPages(responseData.totalPages || 1);
          } else if (Array.isArray(responseData)) {
            // Fallback for non-paginated response
            setPrescriptions(responseData || []);
            setTotalPages(Math.ceil(responseData.length / itemsPerPage));
          }
        }
      } catch (error) {
        console.error("Error fetching prescriptions:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPrescriptions();
    
    return () => {
      hasFetched.current = false;
    };
  }, [appointmentId, patientId, userData.token]);

  if (loading) {
    return <div style={{ textAlign: "center", padding: "20px" }}><CircularProgress /></div>;
  }

  return (
    <Box sx={{ padding: "20px", position: "relative" }}>
      {prescriptions.length > 0 ? (
        <>
          <Box sx={{ position: "relative", paddingLeft: "40px" }}>
            <Box sx={{ position: "absolute", left: "15px", top: 0, bottom: 0, width: "2px", backgroundColor: "#2AAB6C" }} />
            {prescriptions.map((item, index) => (
            <Box key={item._id} sx={{ position: "relative", marginBottom: "30px" }}>
              <Box sx={{ position: "absolute", left: "-31px", top: "5px", width: "12px", height: "12px", borderRadius: "50%", backgroundColor: "#2AAB6C", border: "3px solid white", boxShadow: "0 0 0 2px #2AAB6C" }} />
              <Paper elevation={2} sx={{ padding: "16px", borderRadius: "8px" }}>
                <Typography variant="subtitle2" sx={{ color: "#666", marginBottom: "8px" }}>
                  {moment(item.updatedAt).format("MMMM DD, YYYY")}
                </Typography>
                <Typography variant="h6" sx={{ marginBottom: "8px", fontWeight: 600 }}>
                  {item.doctorInfo?.name || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#666", marginBottom: "8px" }}>
                  Department: {item.doctorInfo?.department || "N/A"}
                </Typography>
                <Typography variant="body2" sx={{ color: "#333", marginBottom: "12px", fontStyle: "italic" }}>
                  Note: {item.note || "----"}
                </Typography>
                <a href={item.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color: "#2AAB6C", textDecoration: "none", fontWeight: 500 }}>
                  View Prescription →
                </a>
              </Paper>
            </Box>
          ))}
          </Box>
          {totalPages > 1 && (
            <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
              <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" />
            </Box>
          )}
        </>
      ) : (
        <Typography sx={{ textAlign: "center", color: "#666", padding: "40px" }}>No prescriptions found</Typography>
      )}
    </Box>
  );
};

export default Timeline;
